import type { FinancialSectionV2, SalesTaxV2 } from "@credit-acceptance/step6-bff-client";

import { sumAmounts } from "../../../../lib/money";
import { calculateTotalDownPaymentDeduction, calculateTotalSellingPrice } from "./Financial.calculations";

export interface ItemizationAmounts {
  sellingPrice?: number;
  accessories?: number;
  totalSellingPrice?: number;
  /** Always non-positive: the rebate subtracts from the amount financed. */
  rebate?: number;
  salesTax?: number;
  isSalesTaxManual: boolean;
  cashDown?: number;
  grossTradeIn?: number;
  /** Always non-positive: the payoff subtracts from the down payment group. */
  tradeInPayoff?: number;
  /** Negative deduction, undefined when the response has no down payment figures. */
  totalDownPayment?: number;
  vsc?: number;
  gap?: number;
  otherBackendProducts?: number;
  totalCustomerFees?: number;
  amountFinanced?: number;
}

/**
 * The stored total wins; otherwise the sum of the tax rows. No rows at all gives 0, an absent section gives `undefined`.
 * @param salesTax - The `salesTax` block of the itemization.
 * @returns {number | undefined} The total sales tax.
 */
function resolveSalesTaxTotal(salesTax?: SalesTaxV2): number | undefined {
  if (salesTax?.total !== undefined) {
    return salesTax.total;
  }
  if (salesTax?.itemization === undefined) {
    return undefined;
  }
  return sumAmounts(salesTax.itemization.map((line) => ({ amount: line.taxAmount ?? 0 })));
}

/**
 * The BFF value wins; otherwise Selling Price + Accessories price (absent accessories count as 0).
 * A missing Selling Price yields `undefined` rather than a total derived from nothing.
 * @param financial - The `financial` section of the v2 dashboard response.
 * @returns {number | undefined} The Total Selling Price.
 */
function resolveTotalSellingPrice(financial?: FinancialSectionV2): number | undefined {
  const bffValue = financial?.itemization?.totalSellingPrice;
  if (bffValue !== undefined) {
    return bffValue;
  }
  const sellingPrice = financial?.itemization?.sellingPrice;
  if (sellingPrice === undefined) {
    return undefined;
  }
  return calculateTotalSellingPrice([{ amount: sellingPrice }, { amount: financial?.accessories?.totalPrice ?? 0 }]);
}

/**
 * Flattens the Itemization of Amount Financed figures out of the v2 financial section.
 * Every figure stays `undefined` when the response omits it so the UI can show a dash.
 * @param financial - The `financial` section of the v2 dashboard response.
 * @returns {ItemizationAmounts} The itemization amounts, in display sign convention.
 */
export function mapItemization(financial?: FinancialSectionV2): ItemizationAmounts {
  const itemization = financial?.itemization;
  const downPayment = itemization?.downPayment;
  const payoff = downPayment?.tradeInPayoff;
  const tradeInPayoff = payoff === undefined ? undefined : -Math.abs(payoff);

  const hasDownPayment =
    downPayment?.cashDown !== undefined || downPayment?.grossTradeIn !== undefined || payoff !== undefined;

  return {
    sellingPrice: itemization?.sellingPrice,
    accessories: financial?.accessories?.totalPrice,
    totalSellingPrice: resolveTotalSellingPrice(financial),
    rebate: financial?.rebate === undefined ? undefined : -Math.abs(financial.rebate),
    salesTax: resolveSalesTaxTotal(itemization?.salesTax),
    isSalesTaxManual: itemization?.salesTax?.isManual === true,
    cashDown: downPayment?.cashDown,
    grossTradeIn: downPayment?.grossTradeIn,
    tradeInPayoff,
    totalDownPayment: hasDownPayment
      ? calculateTotalDownPaymentDeduction([
          { amount: downPayment?.cashDown ?? 0 },
          { amount: downPayment?.grossTradeIn ?? 0 },
          { amount: tradeInPayoff ?? 0 },
        ])
      : undefined,
    vsc: itemization?.vsc,
    gap: itemization?.gap,
    otherBackendProducts: itemization?.obpSellingPrice,
    totalCustomerFees: financial?.totalCustomerFees,
    amountFinanced: itemization?.amountFinanced,
  };
}
